import { api } from "@/api/utils";
import { ChartBox, formatAxisNumber, useContainerSize } from "@/components/charts/ChartBox";
import { TokenCurrencyCode, tokensToMoney } from "@/modules/money-fns";
import { formatDollars } from "@common/utils/i18n";
import { Box, BoxProps, Flex, Grid, Text } from "@givenwell/components";
import { styled } from "@givenwell/stitches";
import { colors, fonts } from "@givenwell/theme";
import { areaY, defineChart, rect, ruleY } from "@tanstack/charts";
import { Chart } from "@tanstack/charts/react";
import { scaleLinear } from "@tanstack/charts/scales/linear";
import { tooltip } from "@tanstack/charts/tooltip";
import { queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { scaleUtc } from "d3-scale";
import { ReactNode, Suspense, useMemo } from "react";
import { getCumulativeChartDataByDay } from "./utils";

const totalRevenueQuery = queryOptions({
  queryKey: ["reporting", "total-revenue"],
  queryFn: async () => {
    return await api.reporting.getManagementReportingObsTotalRevenue();
  },
});

export const Route = createFileRoute("/dashboard/cash-flow")({
  component: Page,
});

function Page() {
  return (
    <Grid
      css={{
        width: "100%",
        height: "100%",
        gap: 12,
        gridCols: 2,
        gridTemplateRows: "repeat(1, 1fr)",
      }}
    >
      <Suspense fallback={<Card />}>
        <TotalRevenue />
      </Suspense>
      <Flex css={{ flexDirection: "column", gap: "1vw" }}>
        <Suspense fallback={<Card css={{ flex: "1 0 0px" }} />}>
          <RecurringAllowances />
        </Suspense>
      </Flex>
    </Grid>
  );
}

const sum = (arr: number[]) => arr.reduce((a, b) => a + b, 0);

function tokensToDollars(tokens: number, tokenCurrencyCode: string) {
  return tokensToMoney({ amount: tokens, tokenCurrencyCode: tokenCurrencyCode as TokenCurrencyCode }).amount;
}

/**
 * Despite the keys being token currency codes, these are actually the conversion rates of dollars, not tokens
 */
const LOCAL_TO_NZD: Record<string, number> = {
  autoken: 1.2,
  auarrears: 1.2,
  catoken: 1.22,
  eutoken: 1.96,
  gbtoken: 2.29,
  nztoken: 1,
  nzarrears: 1,

  aetoken: 0.46,
  cntoken: 0.25,
  hktoken: 0.22,
  iltoken: 0.57,
  mxtoken: 0.1,
  sgtoken: 1.33,
  ustoken: 1.69,
  usarrears: 1.69,
} satisfies Record<TokenCurrencyCode, number>;

function convertToNZD(tokens: number, tokenCurrencyCode: string) {
  const localDollars = tokensToDollars(tokens, tokenCurrencyCode);

  const rate = LOCAL_TO_NZD[tokenCurrencyCode];
  if (rate) {
    return localDollars * rate;
  }
  throw new Error(`Unknown token currency: ${tokenCurrencyCode}`);
}

// The same series order and colors the old Plot charts used:
// topup → red, gift → green, everything else (supporter) → blue.
const purchaseTypeDomain = ["supporter", "gift", "topup"];
const purchaseTypeRange = [colors.blue300, colors.green400, colors.red500];

function TotalRevenue() {
  const { data: revenue } = useSuspenseQuery(totalRevenueQuery);
  const chartData = useMemo(
    () =>
      getCumulativeChartDataByDay(
        revenue,
        d => ({
          date: new Date(d.paid_at),
          value: convertToNZD(d.purchased_tokens, d.token_currency_code),
          type: d.purchase_type,
        }),
        ["supporter", "gift", "topup"],
      ),
    [revenue],
  );

  const supporterTotal = sum(
    revenue
      .filter(r => r.purchase_type === "supporter")
      .map(r => convertToNZD(r.purchased_tokens, r.token_currency_code)),
  );

  const giftingTotal = sum(
    revenue.filter(r => r.purchase_type === "gift").map(r => convertToNZD(r.purchased_tokens, r.token_currency_code)),
  );

  const topupTotal = sum(
    revenue.filter(r => r.purchase_type === "topup").map(r => convertToNZD(r.purchased_tokens, r.token_currency_code)),
  );

  const { ref, height } = useContainerSize();

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          // The series are cumulative per type, so they overlap (painted in
          // input order) rather than stack: explicit y1/y2 opt out of the
          // implicit stacking.
          areaY(chartData, {
            x: "date",
            y1: 0,
            y2: "value",
            z: "type",
            color: "type",
            fillOpacity: 1,
          }),
          ruleY([0], { stroke: colors.outline }),
        ],
        scales: {
          x: {
            scale: scaleUtc,
            grid: true,
          },
          y: {
            scale: scaleLinear,
            grid: false,
            axis: { ticks: { size: 0, format: formatAxisNumber } },
          },
        },
        margin: { left: 50 },
        color: {
          domain: purchaseTypeDomain,
          range: purchaseTypeRange,
        },
        tooltip: {
          use: tooltip,
          format: point => `${point.datum.type} ${point.datum.date.toLocaleDateString()}\n${point.datum.value}`,
        },
      }),
    [chartData],
  );

  return (
    <Card title="All-time Points Purchased" css={{ display: "flex", flexDirection: "column" }}>
      <BigValue>
        {formatDollars(sum(revenue.map(r => convertToNZD(r.purchased_tokens, r.token_currency_code))), "NZD", {
          maximumFractionDigits: 0,
        })}
      </BigValue>
      <ValueDescription>
        {formatDollars(supporterTotal, "NZD", { maximumFractionDigits: 0 })} from employers,{" "}
        {formatDollars(giftingTotal, "NZD", { maximumFractionDigits: 0 })} from gifting,{" "}
        {formatDollars(topupTotal, "NZD", { maximumFractionDigits: 0 })} from topup
      </ValueDescription>
      <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
        {height !== undefined && (
          <Chart definition={definition} height={height} ariaLabel="All-time points purchased" />
        )}
      </ChartBox>
    </Card>
  );
}

const getMonthBounds = () => {
  const now = new Date();
  const startOfLastMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1, 0, 0, 0, 0);
  const startOfThisMonth = new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0);
  return {
    startOfLastMonth,
    startOfThisMonth,
  };
};

const months = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

type MonthlyRevenueRow = {
  monthStart: Date;
  monthEnd: Date;
  type: string;
  total: number;
  y1: number;
  y2: number;
};

function RecurringAllowances() {
  const { data: revenue } = useSuspenseQuery(totalRevenueQuery);

  const { startOfLastMonth, startOfThisMonth } = getMonthBounds();

  const lastMonthRevenueData = revenue.filter(
    r => new Date(r.paid_at) >= startOfLastMonth && new Date(r.paid_at) < startOfThisMonth,
  );
  const thisMonthRevenueData = revenue.filter(r => new Date(r.paid_at) >= startOfThisMonth);

  const lastMonthTotalNZD = sum(lastMonthRevenueData.map(r => convertToNZD(r.purchased_tokens, r.token_currency_code)));
  const thisMonthTotalNZD = sum(thisMonthRevenueData.map(r => convertToNZD(r.purchased_tokens, r.token_currency_code)));

  const lastMonthWasBigger = lastMonthTotalNZD > thisMonthTotalNZD;

  const { ref, height } = useContainerSize();

  const monthlyRows = useMemo(() => {
    // The old chart binned Plot's UTC month intervals over dates shifted so
    // their UTC fields matched local time. Bucketing by the local calendar
    // month, materialised as a UTC month, is identical and keeps the UTC time
    // axis labelling local months.
    const totals = new Map<string, { monthStart: Date; monthEnd: Date; type: string; total: number }>();
    for (const r of revenue) {
      const paidAt = new Date(r.paid_at);
      // Plot grouped this chart's series by fill color, so every purchase
      // type other than topup/gift landed in the blue "supporter" series.
      const type = r.purchase_type === "topup" || r.purchase_type === "gift" ? r.purchase_type : "supporter";
      const monthStart = new Date(Date.UTC(paidAt.getFullYear(), paidAt.getMonth(), 1));
      const key = `${monthStart.getTime()}|${type}`;
      const amount = convertToNZD(r.purchased_tokens, r.token_currency_code);
      const entry = totals.get(key);
      if (entry) {
        entry.total += amount;
      } else {
        totals.set(key, {
          monthStart,
          monthEnd: new Date(Date.UTC(paidAt.getFullYear(), paidAt.getMonth() + 1, 1)),
          type,
          total: amount,
        });
      }
    }

    const byMonth = new Map<number, { monthStart: Date; monthEnd: Date; type: string; total: number }[]>();
    for (const entry of totals.values()) {
      const group = byMonth.get(entry.monthStart.getTime());
      if (group) {
        group.push(entry);
      } else {
        byMonth.set(entry.monthStart.getTime(), [entry]);
      }
    }

    const rows: MonthlyRevenueRow[] = [];
    for (const group of byMonth.values()) {
      // Plot's stack order: "value" — ascending value within each month.
      group.sort((a, b) => a.total - b.total);
      let y = 0;
      for (const entry of group) {
        rows.push({ ...entry, y1: y, y2: y + entry.total });
        y += entry.total;
      }
    }
    return rows;
  }, [revenue]);

  const definition = useMemo(
    () =>
      defineChart({
        marks: [
          rect(monthlyRows, {
            x1: "monthStart",
            x2: "monthEnd",
            y1: "y1",
            y2: "y2",
            color: "type",
            inset: 0.5,
          }),
        ],
        scales: {
          x: {
            scale: scaleUtc,
          },
          y: {
            scale: scaleLinear,
            grid: false,
            axis: { ticks: { format: formatAxisNumber } },
          },
        },
        margin: { left: 50 },
        color: {
          domain: purchaseTypeDomain,
          range: purchaseTypeRange,
        },
      }),
    [monthlyRows],
  );

  return (
    <Card title="Points Purchased by Month" css={{ display: "flex", flexDirection: "column", flex: "1 0 0px" }}>
      <BigValue>
        {lastMonthWasBigger
          ? formatDollars(lastMonthTotalNZD, "NZD", { maximumFractionDigits: 0 })
          : formatDollars(thisMonthTotalNZD, "NZD")}
      </BigValue>
      <ValueDescription>
        {lastMonthWasBigger
          ? `for ${months[startOfLastMonth.getMonth()]}. ${formatDollars(thisMonthTotalNZD, "NZD", {
              maximumFractionDigits: 0,
            })} for ${months[startOfThisMonth.getMonth()]} so far`
          : `for ${months[startOfThisMonth.getMonth()]}`}
      </ValueDescription>
      <ChartBox ref={ref} css={{ height: "100%", flex: "1 0 0px" }}>
        {height !== undefined && (
          <Chart definition={definition} height={height} ariaLabel="Points purchased by month" />
        )}
      </ChartBox>
    </Card>
  );
}

const BigValue = styled("p", {
  fontSize: 72,
  weight: 900,
  align: "center",
  lineHeight: 1,
  fontFamily: fonts.ftHabit,
});

const ValueDescription = styled("p", {
  fontSize: 14,
  weight: 500,
  align: "center",
  lineHeight: 1,
  color: colors.text2,
});

type CardProps = Omit<BoxProps, "title"> & {
  title?: ReactNode;
};
function Card({ title, children, ...props }: CardProps) {
  return (
    <Box
      {...props}
      css={{
        pt: 16,
        rounded: 12,
        bg: "white",
        boxShadow: `0 0 0 1px rgba(0,0,0,0.05)`,
        ...props.css,
      }}
    >
      {title && (
        <Text
          css={{
            fontSize: 25,
            weight: 500,
            lineHeight: 1.25,
            align: "center",
            color: colors.navy,
            mb: 4,
          }}
        >
          {title}
        </Text>
      )}
      {children}
    </Box>
  );
}
